/**
 * Stap 2: lezen. Links de tekst, per alinea met een nummer en, als de
 * leerkracht voorlezen voor deze leerling aanzette, een knop Lees voor;
 * rechts het plaatje, de hulpmiddelen en Anna Maria.
 *
 * Selecteert de leerling iets in een alinea, dan werken de hulpmiddelen op
 * die alinea (hooks/useHulp.js); de selectie staat geel in de tekst, en
 * Wis maakt hem leeg. Welke alinea het is, staat in data-alinea-id.
 *
 * Staat een vertaling in het vak van Anna Maria, en kan de vertaling voor
 * deze leerling worden voorgelezen (voorlezen staat aan, en het kan in zijn
 * moedertaal: `magVertalingVoorlezen`, van de server), dan staat er een knop
 * onder om hem te laten voorlezen.
 *
 * In een groepsles (stap 23, `groepsles`) leest de leerling alleen zijn deel;
 * er is geen toets, en de knop onderaan is Klaar met lezen.
 */
import React from 'react';
import { ChevronRight, HelpCircle, Languages, Lightbulb, MessageCircle, Volume2, VolumeX, X } from 'lucide-react';
import { Draaier } from '../../../components/Laden';
import { vertalingSleutel } from '../hooks/useVoorlezen';
import { OpgemaakteTekst } from '../../../components/OpgemaakteTekst';
import { FOTO } from '../onderdelen/AnnaMaria';
import { Groepje } from '../onderdelen/Groepje';
import { Nummer } from '../onderdelen/Nummer';
import { stijlen } from '../stijlen';

export function Lezen({ titel, alineas, illustratie, moedertaal, hulp, voorlezen, magVoorlezen, magVertalingVoorlezen,
  toetsLaadt, onNaarToets, groepsles, onKlaar }) {
  return (
    <div style={s.raster}>
      <div style={s.tekst}>
        <h2 style={{ marginBottom: groepsles ? 8 : 24, color: 'var(--inkt)' }}>{titel}</h2>
        {groepsles && <p style={{ marginBottom: 24, color: 'var(--tekst-3)' }}>Deel {groepsles.deel} van {groepsles.aantal_delen}</p>}

        {alineas.map((p, i) => (
          <Alinea key={p.id} nummer={i + 1} alinea={p} hulp={hulp} voorlezen={magVoorlezen ? voorlezen : null} />
        ))}

        {groepsles ? (
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <p style={{ fontSize: 16, color: 'var(--inkt)', marginBottom: 16 }}>
              Wat zijn de belangrijkste dingen in jouw deel? Die vertel je straks aan je groepje.
              <br />
              <em>Anna Maria</em>
            </p>
            <Groepje groepsles={groepsles} style={{ marginBottom: 16 }} />
            <button style={{ ...stijlen.groteKnop, opacity: toetsLaadt ? 0.7 : 1 }} onClick={onKlaar} disabled={toetsLaadt}>
              {toetsLaadt ? <><Draaier grootte={20} /> Even wachten...</> : <>Klaar met lezen <ChevronRight size={20} /></>}
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <p style={{ fontSize: 16, color: 'var(--inkt)', marginBottom: 16 }}>
              Heb je de tekst goed gelezen? Ga dan door naar de vragen.
              <br />
              <em>Anna Maria</em>
            </p>
            <button style={{ ...stijlen.groteKnop, opacity: toetsLaadt ? 0.7 : 1 }} onClick={onNaarToets} disabled={toetsLaadt}>
              {toetsLaadt ? <><Draaier grootte={20} /> Toets wordt geladen...</> : <>Naar de toets <ChevronRight size={20} /></>}
            </button>
          </div>
        )}
      </div>

      <div style={s.rechts}>
        {illustratie && (
          <div style={{ ...stijlen.kaart, padding: 12 }}>
            <img src={illustratie} alt="Illustratie bij de tekst" style={{ width: '100%', borderRadius: 8, display: 'block' }} />
          </div>
        )}
        <Hulpmiddelen hulp={hulp} heeftMoedertaal={!!moedertaal} />
        <AnnaMariaKaart hulp={hulp} voorlezen={magVoorlezen && magVertalingVoorlezen ? voorlezen : null} moedertaal={moedertaal} />
      </div>
    </div>
  );
}

/** Een alinea; `voorlezen` is null als de leerkracht voorlezen voor deze leerling niet aanzette (dan geen knop). */
function Alinea({ nummer, alinea, hulp, voorlezen }) {
  const gekozen = hulp.alineaId === alinea.id;
  const speelt = voorlezen?.speelt === alinea.id;
  const laadt = voorlezen?.laadt === alinea.id;
  // Na Vereenvoudig staat de alinea op een ander niveau dan hij binnenkwam: dat is te zien (stap 27).
  const nu = hulp.niveaus?.[alinea.id];
  const vereenvoudigd = !!(nu && alinea.current_niveau && nu !== alinea.current_niveau);
  return (
    <div
      data-testid="alinea"
      data-vereenvoudigd={vereenvoudigd ? 'ja' : undefined}
      style={{
        ...s.alinea,
        ...(gekozen ? { background: 'rgba(74, 144, 164, 0.08)' } : {}),
        // de vervangen alinea in het groen, ook als hij geselecteerd is; voorlezen gaat voor
        ...(vereenvoudigd ? { background: 'var(--goed-vlak)', borderLeft: '4px solid var(--goed-2)' } : {}),
        ...(speelt ? { background: 'rgba(112, 209, 250, 0.15)', borderLeft: '3px solid var(--accent)' } : {}),
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <Nummer n={nummer} grootte={28} />
        {voorlezen && <button
          onClick={(e) => { e.stopPropagation(); voorlezen.leesVoor(alinea.id, alinea.inhoud); }}
          disabled={laadt}
          title={speelt ? 'Stop voorlezen' : 'Lees voor'}
          style={{ ...s.voorleesKnop, background: speelt ? 'var(--accent)' : 'var(--vlak-2)', cursor: laadt ? 'wait' : 'pointer' }}
        >
          {laadt ? <Draaier grootte={16} kleur="var(--accent)" />
            : speelt ? <VolumeX size={16} style={{ color: 'var(--wit)' }} />
              : <Volume2 size={16} style={{ color: 'var(--tekst-3)' }} />}
        </button>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {vereenvoudigd && (
          <div data-testid="makkelijker-gemaakt" style={s.makkelijker}>
            <Lightbulb size={14} /> Makkelijker gemaakt
          </div>
        )}
        <span data-alinea-id={alinea.id}>
          <OpgemaakteTekst tekst={alinea.inhoud} markeer={gekozen ? hulp.selectie : null} />
        </span>
      </div>
    </div>
  );
}

function Hulpmiddelen({ hulp, heeftMoedertaal }) {
  const { selectie, bezig } = hulp;
  return (
    <div style={{ ...stijlen.kaart, padding: 20 }}>
      <h3 style={{ marginBottom: 16, fontSize: 16, color: 'var(--tekst-3)' }}>Hulpmiddelen</h3>

      {selectie && (
        <div style={s.selectie} data-testid="selectie">
          <span><strong>Geselecteerd:</strong> "{selectie.length > 30 ? selectie.substring(0, 30) + '...' : selectie}"</span>
          <button onClick={hulp.wis} title="Wis de selectie" aria-label="Wis de selectie" style={s.wis}>
            <X size={14} /> Wis
          </button>
        </div>
      )}

      <button style={{ ...s.hulpKnop, ...s.blauw }} onClick={hulp.vereenvoudig} disabled={bezig}>
        <Lightbulb size={18} /> Vereenvoudig
      </button>
      {heeftMoedertaal && (
        <button style={{ ...s.hulpKnop, ...s.grijs }} onClick={hulp.vertaal} disabled={bezig}>
          <Languages size={18} /> In mijn moedertaal
        </button>
      )}
      <button style={{ ...s.hulpKnop, ...s.grijs }} onClick={hulp.legUit} disabled={bezig || !selectie}>
        <HelpCircle size={18} /> Uitleg
      </button>
    </div>
  );
}

/** Het vak van Anna Maria; `voorlezen` is null als de vertaling voor deze leerling niet kan worden voorgelezen. */
function AnnaMariaKaart({ hulp, voorlezen, moedertaal }) {
  const sleutel = hulp.vertaling ? vertalingSleutel(hulp.vertaling) : null;
  const speelt = voorlezen && sleutel && voorlezen.speelt === sleutel;
  const laadt = voorlezen && sleutel && voorlezen.laadt === sleutel;
  return (
    <div style={s.annaMaria}>
      <div style={s.annaMariaKop}>
        <img src={FOTO} alt="Anna Maria" style={s.foto} />
        <div>
          <strong style={{ color: 'var(--accent)' }}>Anna Maria</strong>
          <div style={{ fontSize: 12, color: 'var(--tekst-3)' }}>Jouw leeshulp</div>
        </div>
      </div>
      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'var(--inkt)' }}>
        {hulp.bezig ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--tekst-3)' }}>
            <MessageCircle size={16} /> Even denken...
          </div>
        ) : (
          hulp.bericht || 'Selecteer een woord of zin in de tekst en klik op een hulpmiddel om hulp te krijgen!'
        )}
      </div>
      {hulp.onderVertaling && (
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--tekst-3)' }}>{hulp.onderVertaling}</div>
      )}
      {voorlezen && sleutel && !hulp.bezig && (
        <button style={{ ...s.hulpKnop, ...(speelt ? s.blauw : s.wit), marginTop: 16, marginBottom: 0 }}
          onClick={() => voorlezen.leesVertalingVoor(hulp.vertaling, hulp.vertalingNiveau)} disabled={laadt}
          title={speelt ? 'Stop de vertaling' : 'Lees de vertaling voor'}>
          {laadt ? <><Draaier grootte={16} kleur="var(--accent)" /> Even wachten...</>
            : speelt ? <><VolumeX size={18} /> Stop</>
              : <><Volume2 size={18} /> Lees voor in het {moedertaal}</>}
        </button>
      )}
    </div>
  );
}

const s = {
  raster: {
    display: 'grid',
    gridTemplateColumns: '1fr 380px',
    gap: 24,
    maxWidth: 1400,
    margin: '0 auto',
    padding: '24px',
    height: 'calc(100vh - 140px)',
    alignItems: 'stretch',
  },
  tekst: { ...stijlen.kaart, padding: 32, overflowY: 'auto' },
  rechts: { display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' },
  alinea: {
    marginBottom: 20,
    padding: 16,
    borderRadius: 8,
    cursor: 'text',
    transition: 'background 0.2s',
    lineHeight: 1.8,
    fontSize: 17,
    display: 'flex',
    gap: 16,
  },
  makkelijker: {
    display: 'flex',
    width: 'fit-content',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    lineHeight: 1.4,
    fontWeight: 600,
    color: 'var(--wit)',
    background: 'var(--goed-2)',
    borderRadius: 999,
    padding: '2px 10px',
    marginBottom: 6,
    userSelect: 'none',
  },
  voorleesKnop: {
    border: 'none',
    borderRadius: '50%',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    flexShrink: 0,
  },
  selectie: {
    background: 'var(--waarschuw-vlak)',
    border: '1px solid var(--waarschuw)',
    borderRadius: 8,
    padding: '8px 12px',
    marginBottom: 12,
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  wis: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    background: 'var(--wit)',
    border: '1px solid var(--waarschuw)',
    borderRadius: 6,
    padding: '2px 8px',
    fontSize: 12,
    cursor: 'pointer',
    color: 'var(--tekst)',
    flexShrink: 0,
  },
  hulpKnop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '12px 16px',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    width: '100%',
    marginBottom: 8,
    transition: 'all 0.2s',
  },
  blauw: { background: 'var(--accent)', color: 'var(--wit)' },
  grijs: { background: 'var(--vlak-2)', color: 'var(--tekst)' },
  wit: { background: 'var(--wit)', color: 'var(--accent)', border: '1px solid var(--accent)' },
  annaMaria: {
    background: stijlen.annaMariaVerloop,
    borderRadius: 12,
    padding: 20,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 200,
  },
  annaMariaKop: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: '1px solid rgba(74, 144, 164, 0.2)',
  },
  foto: { width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--accent)' },
};
